import { Injectable } from '@angular/core';
import { WagoApiClient } from '@shared/service/wago-api.client';
import { ModelConversionService } from '@shared/convert/model-conversion.service';
import { ImportedCaeProjectDTO } from '../model/vo/imported-cae-project.dto';
import { lastValueFrom, Observable, throwError } from 'rxjs';
import { GenerationResponseVO } from '../manager/generation-response.vo';
import { NumberUtils } from '@shared/util/number-utils';
import { catchError, map } from 'rxjs/operators';
import { StringUtils } from '@shared/util/string-utils';
import { ProjectVO } from '../model/vo/project.vo';

export interface SaveImportedCaeProjectGroupDTO {
  importedCaeProjects: ImportedCaeProjectDTO[];
  parentGroupId: number | undefined;
  projectGroupName: string;
  projectGroupGuid: string;
  importType: string;
  currentProjectName: string;
}

export interface UpdateImportedCaeProjectGroupDTO {
  importedCaeProjects: ImportedCaeProjectDTO[];
  isOverwriteMode: boolean;
  projectGroupId: number;
  projectGroupName: string;
  currentProjectName: string;
}

export interface CaeProject {
  id: string;
  name: string;
  rails: CaeRail[];
}

export interface CaeData {
  exportType: 'Panel' | 'Schema';
  caeType: number;
  exported: string;
  project: CaeProject;
}

export interface CaeConnection {
  level: number | undefined;
  itemId: string;
  connectionPoint: string | undefined;
  termination: string | undefined;
}

export interface CaeBridge {
  //start only frontend
  id: string;
  //end only frontend
  type: 'Saddle' | 'Wire' | 'Other';
  connections: CaeConnection[];
  length: number | undefined;
  crossSection: number | undefined;
  color: string | undefined;
  wireType: string | undefined;
}

export interface CaeMount {
  id: string;
  structure: string;
  name: string;
  offset: { x: number, y: number, z: number };
  size: { width: number, height: number, depth: number };
}

export interface CaeRail extends CaeItem {
  accessory: CaeItem[];
  bridges: CaeBridge[];
  mount: CaeMount | undefined;
}

export interface CaeItem {
  type: 'custom' | 'plc' | string;
  id: string;
  /**
   * Represents the BMK
   */
  structure: string;
  name: string;
  articleNo: string;
  rotation: number;
  offset: { x: number, y: number, z: number };
  size: { width: number, height: number, depth: number };
  items: CaeItem[];
  levels: CaeLevel[];

  //start CAE V1
  space: number;
  //end CAE V1

}

export function createCaeItem(partial: Partial<CaeItem>): CaeItem {
  return {
    type: 'unknown',
    id: StringUtils.createUuid(),
    structure: '',
    name: '',
    articleNo: '',
    items: [],
    offset: {x: 0, y: 0, z: 0},
    size: {width: 0, height: 0, depth: 0},
    levels: [],
    space: 0,
    rotation: 0,
    ...partial
  };
}

export interface CaeLevel {
  name: string;
  number: number | undefined;
}

@Injectable({
  providedIn: 'root'
})
export class CaeDataService {

  private readonly baseUrl: string = 'project-export';
  private readonly exportProjectGroupToCaeUrl: string = `${this.baseUrl}/export-project-group-cae/`;
  private readonly exportProjectToCaeUrl: string = `${this.baseUrl}/export-project-cae/`;
  private readonly checkCaeGenerationResultUrl: string = `${this.baseUrl}/check-cae-result/`;
  private readonly importProjectsFromCaeUrl: string = `${this.baseUrl}/import-projects-cae`;
  private readonly importProjectsFromCaeByStorageUrl: string = `${this.baseUrl}/import-projects-cae-by-storage`;
  private readonly saveImportedCaeProjectGroupUrl: string = `${this.baseUrl}/save-imported-cae-project-group`;
  private readonly updateImportedCaeProjectGroupUrl: string = `${this.baseUrl}/update-imported-cae-project-group`;

  constructor(
    private wagoApiClient: WagoApiClient,
    private modelConversionService: ModelConversionService,
  ) { }

  public exportProjectGroupToCae(data: { selectedId: number, selectedName: string }) : Observable<GenerationResponseVO> {
    if (NumberUtils.isNotNumber(data.selectedId)) {
      const error: Error = new Error('Error: ProjectExportService: "selectedId" is not valid: ' + data.selectedId);
      return throwError(() => error);
    }
    return this.wagoApiClient.get(`${this.exportProjectGroupToCaeUrl}${data.selectedId}`,
      {isErrorHandlingRequired: false}).pipe(
      map((object: any) => this.modelConversionService.convertGenerationResponseVO(object)),
      catchError(response => {
        throw new Error(response.text());
      }));
  }

  public exportProjectToCae(data: { selectedId: number, selectedName: string }): Observable<GenerationResponseVO> {
    if (NumberUtils.isNotNumber(data.selectedId)) {
      const error: Error = new Error('Error: ProjectExportService: "selectedId" is not valid: ' + data.selectedId);
      return throwError(() => error);
    }
    return this.wagoApiClient.get(`${this.exportProjectToCaeUrl}${data.selectedId}`,
      {isErrorHandlingRequired: false}).pipe(
      map((object: any) => this.modelConversionService.convertGenerationResponseVO(object)),
      catchError(response => {
        throw new Error(response.text());
      }));
  }

  public checkCaeGenerationResult(reportId: string): Observable<GenerationResponseVO> {
    if (StringUtils.isEmpty(reportId)) {
      const error: Error = new Error('Error: ProjectExportService: reportId is not valid: ' + reportId);
      const data: any = { error: error, reportId: reportId };
      return throwError(() => data);
    }
    return this.wagoApiClient.get(`${this.checkCaeGenerationResultUrl}${reportId}`, undefined,
      { isErrorHandlingRequired: false }).pipe(
      map((object: any) => this.modelConversionService.convertGenerationResponseVO(object)));
  }

  public importProjectsFromCaeByStorageId(storageId: string): Observable<CaeData> {
    return this.wagoApiClient
      .post(this.importProjectsFromCaeByStorageUrl, storageId, undefined, {isErrorHandlingRequired: false});
  }

  public importProjectsFromCae(data: ArrayBuffer): Observable<CaeData> {
    return this.wagoApiClient
      .post(this.importProjectsFromCaeUrl, data, undefined, {isErrorHandlingRequired: false});
  }

  public saveImportedCaeProjectGroup(dto: SaveImportedCaeProjectGroupDTO): Promise<ProjectVO> {
    return lastValueFrom(this.wagoApiClient.post(this.saveImportedCaeProjectGroupUrl, dto, undefined,
      { isErrorHandlingRequired: false }).pipe(
      map((object: any) => this.modelConversionService.convertProjectVO(object))));
  }

  public updateImportedCaeProjectGroup(dto: UpdateImportedCaeProjectGroupDTO): Promise<ProjectVO> {
    return lastValueFrom(this.wagoApiClient.post(this.updateImportedCaeProjectGroupUrl, dto, undefined,
      {isErrorHandlingRequired: false}).pipe(
      map((object: any) => this.modelConversionService.convertProjectVO(object))));
  }
}
